import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { UseCase } from '../tenants/config';
import { useGetAccountQuery } from '../api/account/accountApiSlice';

interface TenantCapabilities {
    /** Use cases the current tenant offers (empty when config is unresolved). */
    services: UseCase[];
    /** Whether the tenant uses payment & codes (prepaid / promo / goodwill).
     *  Defaults to `true` when unset — most tenants charge; only tenants
     *  explicitly flagged `usesPaymentCodes: false` (e.g. office-storage without
     *  payment) hide the code tabs + the promo-banner type. */
    usesPaymentCodes: boolean;
}

/**
 * Exposes the current tenant's capabilities, derived from the legacy
 * `authentication.config` redux state (same source as `useObjectLimit`).
 * Returns an empty `services` list when the config isn't resolved yet. Callers
 * choose how to read that: display-level callers may show everything rather
 * than lock the user out, while a guard that decides what may be *selected*
 * should fail closed (see `pages/vouchersCodes/components/selectableGroups`).
 * Extended later with a payment flag.
 */
export function useTenantCapabilities(): TenantCapabilities {
    const config = useSelector((state: RootState) => state.authentication.config);
    // Live per-tenant "uses payment" flag from operational config (#1335 §3).
    // Self-service tenants have no static config file, so the static
    // `usesPaymentCodes` is undefined for them and they previously defaulted to
    // `true`; prefer the value surfaced by getAccount. Falls back to the static
    // per-tenant config (pinned/legacy tenants), then to `true`. `false` is
    // preserved (nullish coalescing), so a non-charging tenant hides the tabs.
    const { data: account } = useGetAccountQuery();
    const liveUsesPayment =
        account && 'tenant' in account ? account.tenant.usesPayment : undefined;
    return {
        services: config?.services ?? [],
        usesPaymentCodes: liveUsesPayment ?? config?.usesPaymentCodes ?? true,
    };
}
